/**
 * @sazeo/sdk — Vacations hooks
 *
 * Hooks for the vacations addon. All requests carry the caller's Supabase JWT.
 * RLS enforces tenant isolation — no direct supabase.from() here.
 *
 * Paired spec: specs/CODEX_VACATIONS_ADDON.md
 */

import { type TenantSupabaseClient, getTenantClient } from '@sazeo/supabase-tenant';
import { useCallback, useEffect, useRef, useState } from 'react';

// ---------------------------------------------------------------------------
// Row types
// ---------------------------------------------------------------------------

export interface LeaveTypeRow {
  id: string;
  tenant_id: string;
  name: string;
  days_per_year: number;
  carry_over_days: number;
  requires_approval: boolean;
  color: string | null;
  created_at: string;
}

export interface LeaveBalanceRow {
  id: string;
  tenant_id: string;
  user_id: string;
  leave_type_id: string;
  year: number;
  allocated_days: number;
  used_days: number;
  pending_days: number;
  carried_over_days: number;
  effective_days: number;
  leave_type?: LeaveTypeRow;
}

export type LeaveStatus = 'pending' | 'approved' | 'rejected' | 'cancelled';

export interface LeaveRequestRow {
  id: string;
  tenant_id: string;
  user_id: string;
  leave_type_id: string;
  start_date: string;
  end_date: string;
  days_requested: number;
  status: LeaveStatus;
  notes: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  rejection_reason: string | null;
  created_at: string;
  updated_at: string;
  leave_type?: LeaveTypeRow;
}

export interface LeaveRequestFilters {
  userId?: string;
  status?: LeaveStatus;
  year?: number;
}

export interface LeaveRequestCreateInput {
  leave_type_id: string;
  start_date: string;
  end_date: string;
  days_requested: number;
  notes?: string | null;
}

export interface ReviewInput {
  status: 'approved' | 'rejected';
  rejection_reason?: string;
}

// ---------------------------------------------------------------------------
// Internal fetch helpers — mirrors tasks.ts pattern
// ---------------------------------------------------------------------------

interface SupabaseAuthLike {
  auth: {
    getSession: () => Promise<{ data: { session: { access_token: string } | null } }>;
  };
}

function resolveClient(): SupabaseAuthLike {
  try {
    return getTenantClient() as unknown as TenantSupabaseClient as unknown as SupabaseAuthLike;
  } catch {
    return { auth: { getSession: async () => ({ data: { session: null } }) } };
  }
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const client = resolveClient();
  const { data } = await client.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = await getAuthHeaders();
  const res = await fetch(`/functions/v1/${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...headers, ...init?.headers },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? res.statusText);
  }
  if (res.status === 204) return {} as T;
  return res.json() as Promise<T>;
}

function useAsyncQuery<T>(
  fn: () => Promise<T>,
  initial: T,
  deps: readonly unknown[],
  enabled = true,
): { data: T; isLoading: boolean; error: Error | null; refetch: () => void } {
  const [data, setData] = useState<T>(initial);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: caller supplies a stable dependency list for the lightweight query helper
  const run = useCallback(() => {
    if (!enabled) return;
    setIsLoading(true);
    setError(null);
    fn()
      .then((d) => {
        if (mountedRef.current) setData(d);
      })
      .catch((e) => {
        if (mountedRef.current) setError(e instanceof Error ? e : new Error(String(e)));
      })
      .finally(() => {
        if (mountedRef.current) setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  // biome-ignore lint/correctness/useExhaustiveDependencies: rerun follows the caller-supplied dependency list
  useEffect(() => {
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, isLoading, error, refetch: run };
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useLeaveTypes(_supabase: unknown, tenantId: string) {
  return useAsyncQuery<LeaveTypeRow[]>(
    () => apiFetch<LeaveTypeRow[]>(`addons/vacations/types?tenant_id=${tenantId}`),
    [],
    [tenantId],
    Boolean(tenantId),
  );
}

export function useLeaveRequests(
  _supabase: unknown,
  tenantId: string,
  filters?: LeaveRequestFilters,
) {
  const params = new URLSearchParams({ tenant_id: tenantId });
  if (filters?.userId) params.set('user_id', filters.userId);
  if (filters?.status) params.set('status', filters.status);
  if (filters?.year) params.set('year', String(filters.year));
  const query = params.toString();

  return useAsyncQuery<LeaveRequestRow[]>(
    () => apiFetch<LeaveRequestRow[]>(`addons/vacations/requests?${query}`),
    [],
    [tenantId, filters?.userId ?? null, filters?.status ?? null, filters?.year ?? null],
    Boolean(tenantId),
  );
}

export function useMyLeaveBalance(
  _supabase: unknown,
  tenantId: string,
  userId: string,
  year?: number,
) {
  const resolvedYear = year ?? new Date().getFullYear();
  return useAsyncQuery<LeaveBalanceRow[]>(
    () =>
      apiFetch<LeaveBalanceRow[]>(
        `addons/vacations/balance?tenant_id=${tenantId}&user_id=${userId}&year=${resolvedYear}`,
      ),
    [],
    [tenantId, userId, resolvedYear],
    Boolean(tenantId) && Boolean(userId),
  );
}

export function useMyLeaveBalanceEffective(
  supabase: unknown,
  tenantId: string,
  userId: string,
  year?: number,
) {
  return useMyLeaveBalance(supabase, tenantId, userId, year);
}

export function useCreateLeaveRequest(_supabase: unknown) {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(async (input: LeaveRequestCreateInput) => {
    setIsPending(true);
    setError(null);
    try {
      return await apiFetch<LeaveRequestRow>('addons/vacations/requests', {
        method: 'POST',
        body: JSON.stringify(input),
      });
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      setError(e);
      throw e;
    } finally {
      setIsPending(false);
    }
  }, []);

  return { mutate, isPending, error };
}

export function useReviewLeaveRequest(_supabase: unknown) {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(async (input: { requestId: string; review: ReviewInput }) => {
    setIsPending(true);
    setError(null);
    try {
      return await apiFetch<LeaveRequestRow>(
        `addons/vacations/requests/${input.requestId}/review`,
        {
          method: 'PATCH',
          body: JSON.stringify(input.review),
        },
      );
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      setError(e);
      throw e;
    } finally {
      setIsPending(false);
    }
  }, []);

  return { mutate, isPending, error };
}

export function useCancelLeaveRequest(_supabase: unknown) {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(async (requestId: string) => {
    setIsPending(true);
    setError(null);
    try {
      return await apiFetch<LeaveRequestRow>(`addons/vacations/requests/${requestId}/cancel`, {
        method: 'PATCH',
      });
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      setError(e);
      throw e;
    } finally {
      setIsPending(false);
    }
  }, []);

  return { mutate, isPending, error };
}

// ---------------------------------------------------------------------------
// LeaveBalanceEffective — shape returned by the leave_balance_effective view
// ---------------------------------------------------------------------------

export interface LeaveBalanceEffective {
  id: string;
  tenant_id: string;
  user_id: string;
  leave_type_id: string;
  year: number;
  allocated_days: number;
  used_days: number;
  pending_days: number;
  carried_over_days: number;
  effective_days: number; // allocated_days + carried_over_days
  leave_type?: LeaveTypeRow;
}

export interface ResetYearResponse {
  success: boolean;
  message: string;
}

export function useResetLeaveBalances(_supabase: unknown) {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(async (year: number) => {
    setIsPending(true);
    setError(null);
    try {
      return await apiFetch<ResetYearResponse>('addons/vacations/reset-year', {
        method: 'POST',
        body: JSON.stringify({ year }),
      });
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      setError(e);
      throw e;
    } finally {
      setIsPending(false);
    }
  }, []);

  return { mutate, isPending, error };
}

// Alias — matches the mock name used in LeaveBalanceCarryover.test.tsx
export const useResetLeaveBalanceYear = useResetLeaveBalances;

// ---------------------------------------------------------------------------
// Convenience aliases — spec B19 API surface
// ---------------------------------------------------------------------------

/**
 * Alias for useMyLeaveBalanceEffective.
 * Returns the effective leave balance for a user in the current (or given) year.
 * Each row includes allocated_days, carried_over_days, and effective_days.
 */
export const useLeaveBalanceSummary = useMyLeaveBalanceEffective;

/**
 * Returns year-over-year leave balance history for a user.
 * Reads from the leave_balance_effective view filtered across all years.
 */
export function useLeaveBalanceHistory(_supabase: unknown, tenantId: string, userId: string) {
  return useAsyncQuery<LeaveBalanceEffective[]>(
    () =>
      apiFetch<LeaveBalanceEffective[]>(
        `addons/vacations/balance?tenant_id=${tenantId}&user_id=${userId}&all_years=true`,
      ),
    [],
    [tenantId, userId],
    Boolean(tenantId) && Boolean(userId),
  );
}
